/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { styled } from "@mui/material"
import {
  DataGrid,
  type GridPaginationModel,
  type GridRowClassNameParams,
  GridToolbar,
  type GridValidRowModel,
  useGridApiRef
} from "@mui/x-data-grid"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import HeaderAppearance from "@components/grid-table/header-appearance"

import { BasicTableProps, SortModel } from "./model"

const MyDataGrid = styled(DataGrid)(() => ({
  "& .MuiDataGrid-menuIconButton": {
    display: "none"
  },
  "& .MuiDataGrid-columnSeparator--sideRight": {
    display: "none"
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 900
  },
  "& .MuiDataGrid-root": {
    border: "none"
  },
  "& .MuiDataGrid-root--densityStandard": {
    border: "none"
  },
  "& .MuiDataGrid-root--densityComfortable": {
    border: "none"
  },
  "& .MuiDataGrid-root--densityCompact": {
    border: "none"
  },
  "& .MuiTablePagination-toolbar ": {
    alignItems: "baseline"
  },
  "& .MuiDataGrid-selectedRowCount": {
    color: "transparent"
  },
  // '& .MuiDataGrid-columnHeader:focus-within': {
  //   outline: 'none'
  // },,
  "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
    outline: "none !important"
  },
  "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
    {
      outline: "none !important"
    }
}))

export default function GridTable<R extends GridValidRowModel>({
  rows,
  columns,
  loading,
  paginationHidden = false,
  onPaginationModelChange,
  onSortModelChange,
  rowCount = 0,
  disableColumnSelector = false,
  toolBarIsShow = false,
  disableHeaderAppearance = false,
  paginationMode = "server",
  sortable = false,
  // localKey,
  onRowClick,
  getRowClassName,
  onRowDoubleClick,
  getRowId,
  defaultPageModel = {
    pageSize: 20,
    page: 0
  },
  onRowSelectionModelChange,
  checkboxSelection = false,
  noRowsTableText = "no_data_found"
}: BasicTableProps<R>) {
  const { t } = useTranslation()
  const apiRef = useGridApiRef()
  const [paginationModel, setPaginationModel] = useState(defaultPageModel)
  const [prevRowCount, setPrevRowCount] = useState(rowCount)

  useEffect(() => {
    if (rowCount > 0 || !loading) {
      setPrevRowCount(rowCount)
    }
  }, [rowCount, loading])

  const displayRowCount = rowCount === 0 && loading ? prevRowCount : rowCount
  // const [columnVisible, setColumnVisible] = useLocalStorage<any>(
  //   `columnVisible-${localKey}`,
  //   []
  // )

  const newColumns = columns.map((column) => {
    const newColumn = {
      ...column,
      sortable
    }

    if (column.field === "rightIcon" && !disableHeaderAppearance) {
      newColumn.renderHeader = () => {
        if (!apiRef) return
        return <HeaderAppearance columns={columns} apiRef={apiRef} />
      }
      return newColumn
    }

    return newColumn
  })

  const handlePageChange = (params: GridPaginationModel) => {
    setPaginationModel(params)
    onPaginationModelChange && onPaginationModelChange(params)
  }

  useEffect(() => {
    setPaginationModel((prev) => {
      if (
        prev.page !== defaultPageModel?.page ||
        prev.pageSize !== defaultPageModel?.pageSize
      ) {
        return defaultPageModel
      }
      return prev
    })
  }, [defaultPageModel?.page, defaultPageModel?.pageSize])

  return (
    <div
      style={{
        width: "calc(100% - 40px)",
        height: paginationHidden ? "auto" : "calc(100vh - 140px)"
      }}
    >
      <MyDataGrid
        rows={rows?.filter((row): row is R => !!row) ?? []}
        // columns={columns.map((column) => ({
        //   ...column,
        //   sortable
        // }))}
        columns={newColumns}
        apiRef={apiRef}
        getRowId={getRowId && ((row) => getRowId(row as R))}
        checkboxSelection={checkboxSelection}
        paginationMode={paginationMode}
        pageSizeOptions={[20, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        autoHeight={paginationHidden}
        style={{ border: "none" }}
        disableColumnMenu={true}
        disableRowSelectionOnClick
        disableColumnSelector={!disableColumnSelector}
        hideFooterPagination={paginationHidden}
        loading={loading}
        rowCount={displayRowCount}
        onRowSelectionModelChange={onRowSelectionModelChange}
        onSortModelChange={(model) =>
          onSortModelChange && onSortModelChange(model as SortModel[])
        }
        getRowClassName={
          getRowClassName &&
          ((params) => getRowClassName(params as GridRowClassNameParams<R>))
        }
        onRowClick={(params) => {
          onRowClick && onRowClick(params?.row)
        }}
        onRowDoubleClick={(params) => {
          onRowDoubleClick && onRowDoubleClick(params?.row)
        }}
        slots={{
          toolbar: toolBarIsShow ? GridToolbar : undefined,
          moreActionsIcon: MoreHorizIcon
          // pagination: RoundedPagination
        }}
        localeText={{
          toolbarDensity: t("gridTable.toolbarDensity"),
          toolbarDensityCompact: t("gridTable.toolbarDensityCompact") || "",
          toolbarDensityStandard: t("gridTable.toolbarDensityStandard") || "",
          toolbarDensityComfortable:
            t("gridTable.toolbarDensityComfortable") || "",
          toolbarColumns: t("gridTable.toolbarColumns"),
          toolbarColumnsLabel: t("gridTable.toolbarColumnsLabel") || "",
          toolbarExport: t("gridTable.toolbarExport") || "",
          toolbarExportLabel: t("gridTable.toolbarExportLabel") || "",
          toolbarExportCSV: t("gridTable.toolbarExportCSV") || "",
          toolbarExportPrint: t("gridTable.toolbarExportPrint") || "",
          toolbarFilters: t("gridTable.toolbarFilters") || "",
          toolbarFiltersTooltipActive(count) {
            return t("gridTable.toolbarFiltersTooltipActive", { count })
          },
          noRowsLabel: t(noRowsTableText) || "",
          MuiTablePagination: {
            labelRowsPerPage: "",
            labelDisplayedRows: ({ from, to, count }) => {
              return `${from}-${to} / ${count}`
              // return t("gridTable.muiTablePagination.labelDisplayedRows", {
              //   from,
              //   to,
              //   count
              // })
            }
          }
        }}
      />
    </div>
  )
}
